import React, { useEffect, useState } from "react";
import { EditIcon, SaveIcon } from "assets/icons";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import Button from "components/BaseWidgets/button";
import { partnerPreference as partnerPreferenceFields } from "components/MyProfile/helper";
import Slider from "components/BaseWidgets/slider";
import {
  useSelectOptions,
  useProfession,
  useIncomes,
  useCommunities,
} from "hooks/api/useListing";
import { mapToSelectOptions } from "utils/formatHelper";
import {
  drinking,
  smoking,
  selectBodyHeight,
  selectBodyWeight,
} from "components/Onboard/components/helper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { useDistrict, useStates, useTown } from "hooks/api/useListing";

function PartnerPreferences({ isEdit, id, partnerPreference = {} }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  const queryClient = useQueryClient();
  const editProfileMutation = useEditProfile();

  const screenTypes = [
    "nationality",
    "religion",
    "qualification",
    "diet",
    "maritalStatus",
    "familyStatus",
    "complexion",
    "bodyType",
    "familyType",
    "profession",
    "incomes",
  ];

  const {
    nationality,
    religion,
    qualification,
    diet,
    maritalStatus,
    familyStatus,
    complexion,
    bodyType,
    familyType,
  } = useSelectOptions({ screenTypes });

  // APIs

  const { data: professionData } = useProfession({
    search: "",
    screen: screenTypes.includes("profession") ? 1 : undefined,
  });
  const selectProfession = mapToSelectOptions(professionData || []);
  const { data: incomeData } = useIncomes({
    search: "",
    screen: screenTypes.includes("incomes") ? 1 : undefined,
  });
  const selectedNationalities = formData["country"] || [];
  const countryIds = Array.isArray(selectedNationalities)
    ? selectedNationalities
    : [selectedNationalities].filter(Boolean);
  const { data: stateData, isLoading: isStatePending } = useStates({
    search: "",
    country_id: countryIds || "",
  });
  const selectState = mapToSelectOptions(stateData);
  const stateIds = formData["city"] || [];
  const parentIds = Array.isArray(stateIds)
    ? stateIds
    : [stateIds].filter(Boolean);
  const { data: districtData, isLoading: isDistrictPending } = useDistrict({
    search: "",
    parent_id: parentIds,
  });
  const selectDistrict = mapToSelectOptions(districtData);

  // const selectTown = mapToSelectOptions(townData);
  const selectAnnualIncome = mapToSelectOptions(incomeData);
  const selectedReligionId =
    formData["religion"]?.id || formData["religion"] || null;
  const { data: communityData, isLoading: isCommunityPending } = useCommunities(
    {
      search: "",
      parent_id: selectedReligionId, // Use the selected religion ID
      screen: 1,
    },
  );
  const CommunityData = mapToSelectOptions(communityData);
  // Group all options for easy lookup
  const allOptions = {
    // Single Select
    Religion: religion?.options,
    Smoking: smoking,
    Drinking: drinking,
    // Multi Select
    Nationality: nationality?.options,
    Qualification: qualification?.options,
    Profession: selectProfession,
    "Annual Income": selectAnnualIncome,
    Diet: diet?.options,
    "Marital status": maritalStatus?.options,
    Complexion: complexion?.options,
    "Body type": bodyType?.options,
    "Family Status": familyStatus?.options,
    "Family type": familyType?.options,
    State: selectState,
    District: selectDistrict,
    // Range Options (used for sliders)
    Height: selectBodyHeight,
    Weight: selectBodyWeight,
    Community: CommunityData,
  };

  useEffect(() => {
    const initialData = {};
    if (!partnerPreference || Object.keys(allOptions).length === 0) {
      return;
    }

    partnerPreferenceFields.forEach((item) => {
      const value = partnerPreference[item.key];

      if (item.key === "height" || item.key === "weight") {
        const options = allOptions[item.label] || [];
        let minIndex = 0;
        let maxIndex = options.length > 0 ? options.length - 1 : 0;

        if (typeof value === "string" && value.includes("-")) {
          const [minStr, maxStr] = value.split("-").map((v) => v.trim());
      
          const extractVal = (str, isHeight) => {
            if (isHeight) {
              // Extract cm explicitly
              const cmMatch = str.match(/(\d+(?:\.\d+)?)\s*cm/i);
              if (cmMatch) return Math.round(parseFloat(cmMatch[1]));
              
              // Extract feet/inches and convert to cm
              const ftMatch = str.match(/(\d+)\s*(?:ft|')/i);
              const inMatch = str.match(/(\d+)\s*(?:in|")/i);
              if (ftMatch) {
                const ft = parseInt(ftMatch[1], 10);
                const inches = inMatch ? parseInt(inMatch[1], 10) : 0;
                return Math.round((ft * 12 + inches) * 2.54);
              }

              // Fallback to searching for numeric values
              const numbers = str.match(/\d+/g);
              if (numbers) {
                const cmVal = numbers.map(Number).find(n => n >= 90 && n <= 250);
                if (cmVal) return cmVal;
                return parseInt(numbers[0], 10);
              }
            } else {
              const kgMatch = str.match(/(\d+(?:\.\d+)?)\s*kg/i);
              if (kgMatch) return Math.round(parseFloat(kgMatch[1]));
              
              const numbers = str.match(/\d+/g);
              if (numbers) {
                const kgVal = numbers.map(Number).find(n => n >= 30 && n <= 200);
                if (kgVal) return kgVal;
                return parseInt(numbers[0], 10);
              }
            }
            return parseInt(str, 10);
          };

          const minVal = extractVal(minStr, item.key === "height");
          const maxVal = extractVal(maxStr, item.key === "height");

          // Find closest value instead of exact match for robustness
          const findClosestIndex = (options, target) => {
            if (!options.length) return -1;
            let closestIdx = 0;
            let closestDist = Infinity;
            options.forEach((opt, idx) => {
              const dist = Math.abs(parseInt(opt.value, 10) - target);
              if (dist < closestDist) {
                closestDist = dist;
                closestIdx = idx;
              }
            });
            return closestDist <= 3 ? closestIdx : -1;
          };

          const foundMinIndex = findClosestIndex(options, minVal);
          const foundMaxIndex = findClosestIndex(options, maxVal);

          if (foundMinIndex !== -1) minIndex = foundMinIndex;
          if (foundMaxIndex !== -1) maxIndex = foundMaxIndex;
        }
        initialData[item.key] = { min: minIndex, max: maxIndex };
      } else if (item.type === "range") {
        if (typeof value === "string" && value.includes("-")) {
          const [min, max] = value.split("-").map((v) => v.trim());
          initialData[item.key] = { min, max };
        } else {
          initialData[item.key] = { min: "18", max: "70" };
        }
      } else if (item.type === "multiSelect") {
        initialData[item.key] = Array.isArray(value)
          ? value.map((v) => v?.id || v)
          : [];
      } else if (item.type === "select") {
        const rawValue = value?.id || value || "";
        // Convert empty objects to empty strings
        initialData[item.key] = (typeof rawValue === "object" && Object.keys(rawValue).length === 0) ? "" : rawValue;
      } else {
        initialData[item.key] = value || "";
      }
    });

    setFormData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(initialData)) {
        return prev; // no update, avoids re-render loop
      }
      return initialData;
    });
  }, [partnerPreference]);
  const handleChange = (key, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [key]: value };

      if (key === "religion") {
        updated["community"] = null;
      }
      if (key === "country") {
        updated["place"] = "";
        updated["city"] = "";
      } else if (key === "city") {
        updated["place"] = "";
      }

      return updated;
    });
  };

  const preparePayload = () => {
    const cleanedPartnerPreference = {};
    partnerPreferenceFields.forEach((item) => {
      const val = formData[item.key];
      if (item.type === "range") {
        if (item.key === "height" || item.key === "weight") {
          const options = allOptions[item.label] || [];
          let minLabel = options[val?.min]?.label;
          let maxLabel = options[val?.max]?.label;

          if (item.key === "weight") {
            maxLabel = maxLabel?.replace(" kg", " Kg");
            minLabel = minLabel?.replace(" kg", " Kg");
          }

          cleanedPartnerPreference[item.key] =
            minLabel && maxLabel ? `${minLabel}-${maxLabel}` : null;
        } else {
          cleanedPartnerPreference[item.key] =
            val?.min && val?.max ? `${val.min}-${val.max}` : null;
        }
      } else if (item.type === "multiSelect") {
        cleanedPartnerPreference[item.key] = Array.isArray(val) ? val : [];
      } else {
        cleanedPartnerPreference[item.key] = val || null;
      }
    });

    return {
      partner_preference: cleanedPartnerPreference,
    };
  };

  const getDisplayValue = (item) => {
    const value = formData[item.key];
    const options = allOptions[item.label] || [];
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value?.length === 0 ||
      (typeof value === "object" && Object.keys(value).length === 0)
    ) {
      return "-";
    }

    if (item.type === "range") {
      if (item.key === "height" || item.key === "weight") {
        const minLabel = options[value?.min]?.label;
        const maxLabel = options[value?.max]?.label;

        return minLabel && maxLabel ? `${minLabel} - ${maxLabel}` : "-";
      }
      return value?.min && value?.max ? `${value.min} - ${value.max}` : "-";
    }

    if (
      item.type === "multiSelect" &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      return (
        value
          .map((id) => options.find((opt) => opt.value === id)?.label || id)
          .join(", ") || "-"
      );
    }

    if (item.type === "select" && value) {
      return options.find((opt) => opt.value === value)?.label || value;
    }

    return value || "-";
  };
  const handleSave = () => {
    const cleaned = Object.fromEntries(
      Object.entries(formData).map(([key, val]) => {
        return [key, Array.isArray(val) ? val.join(", ") : val];
      }),
    );
    setEditing(false);
    const payload = preparePayload();
    Object.keys(payload).forEach((key) => {
      if (key !== "partner_preference") {
        payload[key] = null;
      }
    });

    editProfileMutation.mutate(payload, {
      onSuccess: () => {
        setEditing(false);
        queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      },
    });
  };

  const renderField = (item) => {
    switch (item.type) {
      case "multiSelect":
        const isDisabled =
          (item.key === "city" &&
            (!formData["country"] || formData["country"].length === 0)) ||
          (item.key === "place" &&
            (!formData["city"] || formData["city"].length === 0));
        return (
          <Select
            SkeletonHeight={24}
            value={
              Array.isArray(formData[item.key])
                ? formData[item.key].filter(Boolean)
                : []
            }
            onChange={(val) => handleChange(item.key, val)}
            options={allOptions[item.label] || []}
            className="item-input-select"
            mode="multiple"
            isDisabled={isDisabled}
          />
        );

      case "select":
        return (
          <Select
            isDisabled={item.label === "Religion"}
            SkeletonHeight={24}
            value={formData[item.key]}
            onChange={(val) => handleChange(item.key, val)}
            options={allOptions[item.label] || []}
            className="item-input-select"
          />
        );

      case "range":
        const rangeValue = formData[item.key] || { min: 0, max: 0 };

        if (item.key === "height" || item.key === "weight") {
          const options = allOptions[item.label] || [];
          if (!options.length) return null;

          const minIndex = rangeValue.min ?? 0;
          const maxIndex = rangeValue.max ?? options.length - 1;

          const minValue = parseInt(options[0]?.value, 10);
          const maxValue = parseInt(options[options.length - 1]?.value, 10);

          const currentValues = [
            parseInt(options[minIndex]?.value, 10),
            parseInt(options[maxIndex]?.value, 10),
          ];

          return (
            <Slider
              footer={false}
              variant={item.key === "height" ? "height" : "weight"}
              range
              minValue={minValue}
              maxValue={maxValue}
              className="item-slider"
              value={currentValues}
              onChange={([minVal, maxVal]) => {
                const newMinIndex = options.findIndex(
                  (opt) => parseInt(opt.value, 10) === minVal,
                );
                const newMaxIndex = options.findIndex(
                  (opt) => parseInt(opt.value, 10) === maxVal,
                );

                handleChange(item.key, {
                  min: newMinIndex !== -1 ? newMinIndex : minIndex,
                  max: newMaxIndex !== -1 ? newMaxIndex : maxIndex,
                });
              }}
            />
          );
        }

        return (
          <Slider
            footer={false}
            variant="age"
            range
            className="item-slider"
            value={[
              parseInt(rangeValue.min, 10) || 18,
              parseInt(rangeValue.max, 10) || 70,
            ]}
            onChange={([min, max]) =>
              handleChange(item.key, { min: String(min), max: String(max) })
            }
          />
        );
      default:
        return (
          <Input
            id={item.label}
            addonAfter={item.suffix}
            value={formData[item.key]}
            onChange={(val) => handleChange(item.key, val)}
            inputClassname="item-input"
          />
        );
    }
  };

  return (
    <div id={id} className="family-panel-frame panel-frame">
      <div className="family-details">
        <div className="header flex items-center justify-between">
          <label className="family-label font-title-normal">
            Partner preference
          </label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => (editing ? handleSave() : setEditing(true))}
              className="edit-button flex items-center gap-1"
            >
              {editing ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div className="content-grid flex flex-col gap-4 pt-4">
          {partnerPreferenceFields.map((item) => (
            <div key={item?.key} className="item-frame flex flex-col gap-1">
              <span className="item-label">{item.label}</span>
              {!editing ? (
                <span className="item-value">{getDisplayValue(item)}</span>
              ) : (
                renderField(item)
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PartnerPreferences;
